import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from 'src/app/shared/services/api.service';
import { AiReportParams, AiReportType } from 'src/app/shared/services/ai-reports.service';

function isoToday(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

@Component({
  selector: 'app-ai-report-form',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ai-report-form.component.html',
  styleUrls: ['./ai-report-form.component.css'],
})
export class AiReportFormComponent implements OnInit, OnChanges {
  @Input({ required: true }) type!: AiReportType;
  @Input() preset: Partial<AiReportParams> | null = null;
  @Input() lockProject = false;
  @Input() busy = false;
  @Output() submitted = new EventEmitter<AiReportParams>();
  @Output() projectChange = new EventEmitter<number | null>();

  projects: { id: number; project_name: string }[] = [];
  boqs: any[] = [];
  boms: any[] = [];

  projectId: number | null = null;
  date = isoToday();
  boqId: number | null = null;
  bomId: number | null = null;
  from = isoToday(-30);
  to = isoToday();
  useAi = true;

  constructor(private api: ApiService) {}

  ngOnInit(): void {
    this.applyPreset();
    this.api.getProjectCommonAll({ limit: 1000 }).subscribe({
      next: (res: any) => {
        const list = res?.data || res || [];
        this.projects = Array.isArray(list) ? list : [];
      },
      error: () => (this.projects = []),
    });
    this.loadContextLists();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['preset'] && !changes['preset'].firstChange) {
      this.applyPreset();
      this.loadContextLists();
    }
    if (changes['type'] && !changes['type'].firstChange) {
      this.loadContextLists();
    }
  }

  get projectName(): string {
    return this.projects.find((p) => p.id === this.projectId)?.project_name || (this.projectId ? `Project #${this.projectId}` : '');
  }

  get canSubmit(): boolean {
    if (!this.projectId || this.busy) return false;
    if (this.type === 'deviation' && this.from && this.to && this.from > this.to) return false;
    return true;
  }

  onProjectChange(): void {
    this.boqId = null;
    this.bomId = null;
    this.projectChange.emit(this.projectId);
    this.loadContextLists();
  }

  submit(): void {
    if (!this.canSubmit || !this.projectId) return;
    const params: AiReportParams = { type: this.type, projectId: this.projectId, useAi: this.useAi };
    if (this.type === 'daily') params.date = this.date || null;
    if (this.type === 'boq_summary') params.boqId = this.boqId;
    if (this.type === 'bom_summary') params.bomId = this.bomId;
    if (this.type === 'deviation') {
      params.from = this.from || null;
      params.to = this.to || null;
    }
    this.submitted.emit(params);
  }

  private applyPreset(): void {
    const p = this.preset || {};
    if (p.projectId) this.projectId = Number(p.projectId);
    if (p.date) this.date = String(p.date).slice(0, 10);
    if (p.boqId) this.boqId = Number(p.boqId);
    if (p.bomId) this.bomId = Number(p.bomId);
    if (p.from) this.from = String(p.from).slice(0, 10);
    if (p.to) this.to = String(p.to).slice(0, 10);
  }

  private loadContextLists(): void {
    if (!this.projectId) {
      this.boqs = [];
      this.boms = [];
      return;
    }
    if (this.type === 'boq_summary') {
      this.api.getBoQs({ page: 1, limit: 200, project_id: this.projectId }).subscribe({
        next: (res: any) => (this.boqs = res?.data || []),
        error: () => (this.boqs = []),
      });
    }
    if (this.type === 'bom_summary') {
      this.api.getBoms({ page: 1, limit: 200, project_id: this.projectId }).subscribe({
        next: (res: any) => (this.boms = res?.data || []),
        error: () => (this.boms = []),
      });
    }
  }
}
